import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import type { TranslationKeys } from '../../../i18n';

interface ProjectNameConfirmationModalProps {
  open: boolean;
  originalProjectName: string;
  fileProjectName: string;
  onClose: () => void;
  onChooseOriginal: () => void;
  onChooseNew: () => void;
  loading?: boolean;
}

const ProjectNameConfirmationModal: React.FC<ProjectNameConfirmationModalProps> = ({
  open,
  originalProjectName,
  fileProjectName,
  onClose,
  onChooseOriginal,
  onChooseNew,
  loading = false,
}) => {
  const { t } = useTranslation<TranslationKeys>();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" component="div" color="warning.main">
            ⚠️ {t('modal.project_name_mismatch_title')}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            disabled={loading}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 3 }}>
          {t('modal.project_name_mismatch_message')}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Current Project Name */}
          <Box
            sx={{
              p: 2,
              border: '2px solid #e3f2fd',
              borderRadius: 2,
              backgroundColor: '#f3f9ff',
            }}
          >
            <Typography variant="subtitle2" color="primary" fontWeight={600}>
              {t('modal.current_project_name')}
            </Typography>
            <Typography variant="body1" sx={{ mt: 1, fontWeight: 500 }}>
              "{originalProjectName}"
            </Typography>
          </Box>

          <Divider sx={{ my: 1 }} />

          {/* File Project Name */}
          <Box
            sx={{
              p: 2,
              border: '2px solid #fff3e0',
              borderRadius: 2,
              backgroundColor: '#fffbf5',
            }}
          >
            <Typography variant="subtitle2" color="orange" fontWeight={600}>
              {t('modal.file_project_name')}
            </Typography>
            <Typography variant="body1" sx={{ mt: 1, fontWeight: 500 }}>
              "{fileProjectName}"
            </Typography>
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
          {t('modal.project_name_choice')}
        </Typography>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          variant="outlined"
          color="inherit"
        >
          {t('modal.cancel_operation')}
        </Button>
        
        <Button
          onClick={onChooseOriginal}
          disabled={loading}
          variant="outlined"
          color="primary"
        >
          {t('modal.keep_current_name')}
        </Button>
        
        <Button
          onClick={onChooseNew}
          disabled={loading}
          variant="contained"
          color="warning"
          sx={{
            backgroundColor: '#ff9800',
            '&:hover': { backgroundColor: '#f57c00' },
          }}
        >
          {t('modal.use_new_name')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProjectNameConfirmationModal;
